import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, Button, useStyleConfig} from '@chakra-ui/react'
import {TextTitleDescription} from '../../text-title-description'
import {ChevronRight} from '../../../icons'
import CustomLink from '../../../custom-link'
import {getGlobalLink} from '../../../../utils/contentstack-utils'
import {useGetCategoriesData} from '../../../../custom-api/hooks/use-get-categories-data'
import useDamImage from '../../../../custom-hooks/use-dam-image'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const PromoSlideImageCard = ({title, bodyCopy, button, url, alt, images, tabIndex = 0}) => {
    const styles = useStyleConfig('PromoSlider')
    const categoriesData = useGetCategoriesData()
    const {locale} = useMultiSite()

    const {data: productsData} = useProducts(
        {
            parameters: {
                ids: button?.productsfcc?.data?.length > 0 ? button.productsfcc.data[0].id : '',
                expand: ['availability', 'images'],
            },
            headers: {'x-debug': 'promo-slide-img-index'},
        },
        {enabled: button?.productsfcc?.data?.length > 0},
    )

    const {
        url: ctaUrl,
        label: ctaLabel,
        isAbsolute,
    } = getGlobalLink({
        link: button,
        categoriesData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })

    const {url: imageUrl, ratio} = useDamImage(images)
    const imageToDisplay = imageUrl || url

    // If slide is inactive (tabIndex = -1), remove from tab order.
    // If there’s no URL, don’t make it focusable.
    const linkTabIndex = ctaUrl ? tabIndex : -1
    const buttonTabIndex = tabIndex

    return (
        <CustomLink
            isExternal={isAbsolute}
            href={ctaUrl}
            tabIndex={linkTabIndex}
            aria-label={alt || 'swiper image'}
            data-testid="promo-slide-image-card-link"
        >
            <Box
                {...styles.promoSlideImageContainer}
                data-testid={`swiper-slide-image-${title || 'container'}`}
            >
                <Flex
                    __css={{...styles.promoSlideBackground, ...styles.promoSlideBackgroundImage}}
                    backgroundImage={imageToDisplay ? `url(${imageToDisplay})` : 'none'}
                    // This is purely visual; avoid labeling a non-interactive container as if it’s announced.
                    // Keep alt semantics on the actual link label above instead.
                    aspectRatio={{lg: ratio}}
                >
                    <Flex sx={styles.promoSliderContent}>
                        <TextTitleDescription title={title} {...styles.textTitleDescriptionImage}>
                            {bodyCopy}
                        </TextTitleDescription>

                        {ctaLabel && (
                            <Button
                                {...styles.promoSliderCardButton}
                                tabIndex={buttonTabIndex}
                                aria-label={ctaLabel}
                                rightIcon={
                                    <ChevronRight
                                        aria-hidden="true"
                                        {...styles.imageCardChevronIcon}
                                    />
                                }
                            >
                                <Text as="span" {...styles.promoSlideCardTextImage}>
                                    {ctaLabel}
                                </Text>
                            </Button>
                        )}
                    </Flex>
                </Flex>
            </Box>
        </CustomLink>
    )
}

PromoSlideImageCard.propTypes = {
    title: PropTypes.string,
    bodyCopy: PropTypes.string,
    button: PropTypes.object,
    url: PropTypes.string,
    alt: PropTypes.string,
    images: PropTypes.object,
    tabIndex: PropTypes.number,
}

export default PromoSlideImageCard
